import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { AlertTriangle, FileSearch, FileText, Loader2, Lock, RotateCcw, Sparkles, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { reviewEssay, suggestQuotesForGaps, outlineEssay, type EssayReview, type EssayToOutline } from "@/lib/smartessay.functions";
import { useAccess } from "@/hooks/useAccess";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PastDueBanner } from "@/components/PastDueBanner";
import { SourceDropzone } from "@/components/SourceDropzone";
import { EssayReviewView } from "@/components/EssayReviewView";
import { EssayOutlineView } from "@/components/EssayOutlineView";
import { PrivacyBadge } from "@/components/PrivacyBadge";
import { SiteHeader } from "@/components/SiteHeader";
import { StepLabel } from "@/components/StepLabel";
import { MicButton } from "@/components/MicButton";
import {
  AcademicConsentDialog,
  useAcademicConsent,
} from "@/components/AcademicConsentGate";
import { ParentLockGate } from "@/components/ParentLockGate";


export const Route = createFileRoute("/reviewer")({
  validateSearch: (search: Record<string, unknown>) => ({
    try: search.try === "1" || search.try === 1 ? "1" : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Essay Reviewer — SmartEssay" },
      { name: "description", content: "Upload or paste your draft for a structured review: evidence gaps with real cited quotes, paragraph flow analysis, and prioritized improvements." },
      { property: "og:title", content: "Essay Reviewer — SmartEssay" },
      { property: "og:description", content: "Get a structured review of your essay: evidence gaps with real cited quotes, logical flow analysis, and prioritized improvement ideas." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReviewerPage,
});

function ReviewerPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const review = useServerFn(reviewEssay);
  const suggestQuotes = useServerFn(suggestQuotesForGaps);
  const outline = useServerFn(outlineEssay);

  const [mode, setMode] = useState<"review" | "outline">("review");
  const [checking, setChecking] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [tryPassRemembered, setTryPassRemembered] = useState(false);
  const [title, setTitle] = useState("");
  const [essayText, setEssayText] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [prompt, setPrompt] = useState("");
  const [citationStyle, setCitationStyle] = useState<"MLA" | "APA">("MLA");
  const [essayType, setEssayType] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [suggestingQuotes, setSuggestingQuotes] = useState(false);
  const [result, setResult] = useState<EssayReview | null>(null);
  const [outlineResult, setOutlineResult] = useState<EssayToOutline | null>(null);
  const [quotesRequested, setQuotesRequested] = useState(false);
  const access = useAccess(userId);
  const consent = useAcademicConsent();

  // Remember the landing-page "try" pass for this browser session
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (search.try === "1") {
      sessionStorage.setItem("reviewer_try_pass", "1");
      setTryPassRemembered(true);
    } else if (sessionStorage.getItem("reviewer_try_pass") === "1") {
      setTryPassRemembered(true);
    }
  }, [search.try]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/auth" });
      else {
        setUserId(data.session.user.id);
        setChecking(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/auth" });
      else setUserId(session.user.id);
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  // No auto-redirect: users land on the Reviewer page even without an
  // active subscription. The in-page lock banner explains the quota state,
  // and onReview() redirects to /pricing only when they actually try to
  // run a review without access. This keeps the Reviewer tab navigable
  // from the Generator and Tutor.

  const showWelcome = consent.showWelcome;
  useEffect(() => {
    if (!checking && userId) showWelcome();
  }, [checking, userId, showWelcome]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    consent.requireConsent(() => void (mode === "outline" ? onOutline() : onReview()));
  }

  async function onReview() {
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }
    if (essayText.trim().length < 50) {
      return toast.error("Paste at least a few sentences of your essay.");
    }
    setLoading(true);
    setResult(null);
    setOutlineResult(null);
    setQuotesRequested(false);
    try {
      const res = await review({
        data: {
          title: title.trim() || undefined,
          essay_text: essayText.trim(),
          source_text: sourceText.trim() || undefined,
          citation_style: citationStyle,
          essay_type: essayType.trim() || undefined,
          prompt: prompt.trim() || undefined,
        },
      });
      setResult(res.output);
      access.refresh();
      // Smooth scroll to result
      setTimeout(() => {
        document.getElementById("review-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't review your essay.");
    } finally {
      setLoading(false);
    }
  }

  async function onOutline() {
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }
    if (essayText.trim().length < 50) {
      return toast.error("Paste at least a few sentences of your essay.");
    }
    setLoading(true);
    setResult(null);
    setOutlineResult(null);
    setQuotesRequested(false);
    try {
      const res = await outline({
        data: {
          title: title.trim() || undefined,
          essay_text: essayText.trim(),
          prompt: prompt.trim() || undefined,
        },
      });
      setOutlineResult(res.output);
      access.refresh();
      setTimeout(() => {
        document.getElementById("review-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't generate the outline.");
    } finally {
      setLoading(false);
    }
  }


  async function onSuggestQuotes() {
    if (!result || !title.trim()) return;
    const gaps = result.evidence_gaps.map((g) => ({
      paragraph_index: g.paragraph_index,
      excerpt: g.excerpt,
      issue: g.issue,
    }));
    if (gaps.length === 0) {
      toast.info("No evidence gaps to fill — your essay already looks well-supported.");
      setQuotesRequested(true);
      return;
    }
    setSuggestingQuotes(true);
    try {
      const res = await suggestQuotes({
        data: { title: title.trim(), citation_style: citationStyle, gaps },
      });
      setResult((prev) => {
        if (!prev) return prev;
        const byIdx = new Map(res.results.map((r) => [r.paragraph_index, r.quotes]));
        return {
          ...prev,
          evidence_gaps: prev.evidence_gaps.map((g) => {
            const suggested = byIdx.get(g.paragraph_index);
            if (!suggested || suggested.length === 0) return g;
            return {
              ...g,
              suggested_quotes: suggested.map((q) => ({
                quote: q.quote,
                citation: q.citation,
                why_it_fits: q.why_it_fits,
              })),
            };
          }),
        };
      });
      setQuotesRequested(true);
      access.refresh();
      const total = res.results.reduce((n, r) => n + r.quotes.length, 0);
      toast.success(total > 0 ? `Added ${total} suggested quote${total === 1 ? "" : "s"}.` : "No confident quotes found for this work.");
    } catch (err) {
      toast.error((err as Error).message || "Couldn't suggest quotes.");
    } finally {
      setSuggestingQuotes(false);
    }
  }


  if (checking) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  const locked = !access.loading && !access.hasAccess;
  const lockMsg = "Subscribe to keep reviewing essays.";

  return (
    <div className="min-h-screen bg-hero-gradient">
      <PaymentTestModeBanner />
      <PastDueBanner show={access.isPastDue} />
      <AcademicConsentDialog
        open={consent.open}
        onOpenChange={consent.setOpen}
        onAccept={consent.accept}
      />
      <SiteHeader />

      <ParentLockGate userId={userId} toolName="Essay Reviewer">
      <main className="mx-auto max-w-3xl px-6 pb-24 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 border-l-2 border-gold pl-3 text-xs font-medium uppercase tracking-[0.18em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Essay Reviewer
            </div>
            <h1 className="font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
              Essay <em>Reviewer</em>
            </h1>
            <div className="mt-4 h-px w-16 bg-gold/70" aria-hidden />
            <p className="mt-4 max-w-xl text-muted-foreground">
              Drop in your draft. We'll find evidence gaps, map the logical flow, and suggest concrete improvements.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (!window.confirm("Start over? This will clear every field on this page.")) return;
                setEssayText("");
                setSourceText("");
                setTitle("");
                setPrompt("");
                setEssayType("");
                setResult(null);
                setOutlineResult(null);
                setQuotesRequested(false);
                toast.success("Cleared — ready for a new review.");
                setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
              }}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Start over
            </Button>
            <PrivacyBadge className="mt-0" />
          </div>
        </div>

        {!access.loading && !access.isSubscribed && (
          <div className={`mt-6 flex items-center justify-between gap-4 rounded-xl border p-4 ${
            access.hasAccess ? "border-border bg-card" : "border-primary bg-primary/5"
          }`}>
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 text-primary" />
              <div className="text-sm">
                <div className="font-medium">
                  {access.hasAccess
                    ? `Free generation available (${access.freeQuota - access.generationCount} of ${access.freeQuota} left)`
                    : "You've used your free generation"}
                </div>
                <div className="text-muted-foreground">
                  {access.hasAccess ? "Reviews count toward the same free quota as outlines." : "Subscribe to keep reviewing essays."}
                </div>
              </div>
            </div>
            <Button asChild size="sm" variant={access.hasAccess ? "outline" : "default"}>
              <Link to="/pricing">{access.hasAccess ? "See plans" : "Unlock"}</Link>
            </Button>
          </div>
        )}

        {/* Mode toggle: Review vs Outline */}
        <div className="mt-8 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-soft">
          {([
            { id: "review", label: "Review", icon: FileSearch },
            { id: "outline", label: "Outline", icon: FileText },
          ] as const).map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => { setMode(m.id); setResult(null); setOutlineResult(null); setQuotesRequested(false); }}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition ${
                  active ? "bg-primary text-primary-foreground shadow-soft" : "text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={active}
              >
                <Icon className="h-4 w-4" /> {m.label}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {mode === "review"
            ? "Get evidence gaps, flow analysis, and prioritized improvements."
            : "Turn your draft into a clean outline: introduction, body paragraphs, and conclusion."}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-7 rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
          {/* Step 1: essay */}
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <StepLabel n={1}>Your essay draft</StepLabel>
              {(essayText || title || prompt) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEssayText("");
                    setSourceText("");
                    setTitle("");
                    setPrompt("");
                    setEssayType("");
                    setResult(null);
                    setOutlineResult(null);
                    setQuotesRequested(false);
                  }}
                  className="gap-1.5"
                >
                  <X className="h-3.5 w-3.5" /> Clear all
                </Button>
              )}
            </div>
            <SourceDropzone value={essayText} onChange={setEssayText} disabled={locked} lockedMessage={lockMsg} />
            <p className="text-xs text-muted-foreground">
              Paste your draft or drop a .txt, .md, .pdf, or .docx file. Use blank lines to separate paragraphs.
            </p>
          </section>

          {mode === "outline" && (
            <section className="space-y-1.5">
              <StepLabel n={2} htmlFor="outline-title">Source title (optional, unlocks per-claim evidence)</StepLabel>
              <Input
                id="outline-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. The Great Gatsby"
                disabled={locked}
              />
              <p className="text-xs text-muted-foreground">
                Add the work this essay is about so you can pull supporting quotes for each claim in the outline.
              </p>
            </section>
          )}

          {mode === "review" && (
            <>
              {/* Step 2: resource + citation */}
              <section className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <StepLabel n={2} htmlFor="title">Source title</StepLabel>
                  <div className="relative">
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. The Great Gatsby"
                      disabled={locked}
                      className="pr-12"
                    />
                    {!locked && (
                      <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                        <MicButton label="the resource" value={title} onChange={setTitle} />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We use this to suggest real quotes from the resource after your review.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <StepLabel n={3} htmlFor="style">Citation style</StepLabel>
                  <div className="flex gap-2 rounded-lg bg-muted p-1">
                    {(["MLA", "APA"] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => !locked && setCitationStyle(s)}
                        disabled={locked}
                        className={`flex-1 rounded-md px-3 py-2 text-sm transition ${
                          citationStyle === s ? "bg-card shadow-soft" : "text-muted-foreground"
                        } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              {/* Step 4: essay type */}
              <section className="space-y-1.5">
                <StepLabel n={4} htmlFor="essay-type">Essay type (optional)</StepLabel>
                <Input
                  id="essay-type"
                  list="essay-type-options"
                  value={essayType}
                  onChange={(e) => setEssayType(e.target.value)}
                  placeholder="e.g. Argumentative, Literary analysis, Personal narrative…"
                  disabled={locked}
                />
                <datalist id="essay-type-options">
                  <option value="Argumentative" />
                  <option value="Persuasive" />
                  <option value="Expository" />
                  <option value="Literary analysis" />
                  <option value="Rhetorical analysis" />
                  <option value="Compare and contrast" />
                  <option value="Cause and effect" />
                  <option value="Narrative" />
                  <option value="Personal narrative / College essay" />
                  <option value="Descriptive" />
                  <option value="Research paper" />
                  <option value="Reflection" />
                  <option value="Book report" />
                  <option value="DBQ (Document-based question)" />
                  <option value="Lab report" />
                </datalist>
                <p className="text-xs text-muted-foreground">
                  Tell the reviewer what kind of essay this is so feedback matches the genre's conventions.
                </p>
              </section>
            </>
          )}


          {/* Step 5: assignment prompt */}
          <section className="space-y-1.5">
            <StepLabel n={mode === "review" ? 5 : 2} htmlFor="prompt">
              {mode === "review" ? "Assignment prompt (optional, helps a lot)" : "Assignment prompt (optional)"}
            </StepLabel>
            <div className="relative">
              <Textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Paste the exact prompt your teacher gave. We'll evaluate how well your draft answers it."
                className="min-h-[80px] pr-12"
                disabled={locked}
              />
              {!locked && (
                <div className="absolute right-2 top-2">
                  <MicButton label="the assignment prompt" value={prompt} onChange={setPrompt} />
                </div>
              )}
            </div>
          </section>

          {/* Sources — supports multiple files (each upload appends, doesn't replace) */}
          <section className="space-y-2">
            <StepLabel n={mode === "review" ? 6 : 3}>
              Sources (optional — add as many as you need)
            </StepLabel>
            <SourceDropzone
              value={sourceText}
              onChange={setSourceText}
              disabled={locked}
              lockedMessage={lockMsg}
              appendOnUpload
            />
            <p className="text-xs text-muted-foreground">
              Drop one source, then drop another to add it — your existing source won't be overwritten. Each upload is
              labeled so the AI knows which quote came from which source.
            </p>
          </section>




          {locked ? (
            <Button asChild size="lg" className="w-full shadow-glow">
              <Link to="/pricing"><Lock className="mr-2 h-4 w-4" /> {mode === "outline" ? "Subscribe to build outlines" : "Subscribe to review essays"}</Link>
            </Button>
          ) : (
            <Button type="submit" size="lg" disabled={loading} className="w-full shadow-glow">
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {mode === "outline" ? "Building your outline…" : "Reviewing your essay…"}</>
              ) : mode === "outline" ? (
                <><FileText className="mr-2 h-4 w-4" /> Convert to outline</>
              ) : (
                <><FileSearch className="mr-2 h-4 w-4" /> Review my essay</>
              )}
            </Button>
          )}
        </form>

        {outlineResult && (
          <div id="review-result" className="mt-10">
            <EssayOutlineView
              outline={outlineResult}
              title={title.trim() || undefined}
              sourceText={sourceText.trim() || undefined}
              citationStyle={citationStyle}
            />
          </div>
        )}


        {result && (
          <div id="review-result" className="mt-10 space-y-6">
            <EssayReviewView review={result} />

            <div className="rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-semibold">
                    {title.trim() ? <>Suggest quotes from <em>{title.trim()}</em></> : "Suggest quotes from the resource"}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {title.trim()
                      ? "We'll fill in evidence gaps with real quotes we know from this resource."
                      : "Add the resource above to get quote suggestions."}
                  </p>
                </div>
                <Button
                  type="button"
                  size="lg"
                  onClick={onSuggestQuotes}
                  disabled={!title.trim() || suggestingQuotes || quotesRequested}
                  className="shrink-0"
                >
                  {suggestingQuotes ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Suggesting…</>
                  ) : quotesRequested ? (
                    <><Sparkles className="mr-2 h-4 w-4" /> Quotes added</>
                  ) : (
                    <><Sparkles className="mr-2 h-4 w-4" /> Suggest quotes</>
                  )}
                </Button>
              </div>
              <div className="mt-4 flex items-start gap-3 rounded-xl border border-sun/50 bg-sun/15 p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-sun" />
                <p className="text-sm text-foreground">
                  Suggested quotes appear in the <strong>Evidence Gaps</strong> section of your review. They're recalled from the AI's training, not from an uploaded copy, so <strong>always verify against your own copy before citing</strong>.
                </p>
              </div>
            </div>

            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => {
                  setEssayText("");
                  setSourceText("");
                  setTitle("");
                  setPrompt("");
                  setEssayType("");
                  setResult(null);
                  setQuotesRequested(false);
                  setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
                }}
              >
                Review another essay
              </Button>
            </div>
          </div>
        )}
      </main>
      </ParentLockGate>
    </div>
  );
}
